import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { getAuthUser } from "./auth";
import { getCrypto } from "./utility";

const ajax = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL,
});

ajax.interceptors.request.use((request) => {
  const skip = request.skipAuthRefresh || false;
  if (!skip) {
    const { decrypt } = getCrypto();
    const user = getAuthUser(decrypt);
    if (user.isAuthenticated) {
      request.headers.Authorization = `Bearer ${user.accessToken}`;
    }
  }
  return request;
});

// Function that will be called to refresh authorization
const onRefreshAuth = (failedRequest) => {
  const tokenExpired = failedRequest.response.headers["token-expired"] !== null;
  if (!tokenExpired) return Promise.resolve();

  const { encrypt, decrypt } = getCrypto();
  const user = getAuthUser(decrypt);
  return ajax
    .post(
      `api/v1/account/token`,
      {
        grantType: "refresh_token",
        refreshToken: user.refreshToken,
      },
      { skipAuthRefresh: true }
    )
    .then((tokenRefreshResponse) => {
      const userData = { ...user, ...tokenRefreshResponse.data };
      // Encrypt user's details and store in localstorage
      const encText = encrypt(userData);
      localStorage.setItem("ctx", encText);
      failedRequest.response.config.headers["Authorization"] =
        "Bearer " + userData.accessToken;
      return Promise.resolve();
    })
    .catch(() => {
      // Redirect to login page if refresh token is also not valid
      localStorage.clear();
      window.location = "/account/login";
    });
};

const getError = (err) => {
  const msg = "The application has encountered an unknown error.";
  if (err.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const status = err.response.status;
    if (status === 400) {
      return err.response.data || msg;
    }
    if (status === 401 || status === 403) {
      return "You are not authorized to access this resource.";
    }
    if (status === 404) {
      return "The request you are trying to access is not available.";
    }
  } else if (err.request) {
    console.log("Request error: ", err.request);
    // The request was made but no response was received
  } else if (err.message) {
    // Something happened in setting up the request that triggered an Error
    console.log("Message error: ", err.message);
  }
  return msg;
};

const useAjax = () => {
  // Instantiate the interceptor
  createAuthRefreshInterceptor(ajax, onRefreshAuth);

  const methods = {
    get: (url, data, options) =>
      ajax
        .get(url, {
          params: data || {},
          ...(options || {}),
        })
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => Promise.reject(getError(err))),
    post: (url, data, options) =>
      ajax
        .post(url, data || {}, {
          ...(options || {}),
        })
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => Promise.reject(getError(err))),
    put: (url, data, options) =>
      ajax
        .put(url, data || {}, {
          ...(options || {}),
        })
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => Promise.reject(getError(err))),
    del: (url, data, options) =>
      ajax
        .delete(url, {
          data: data || {},
          ...(options || {}),
        })
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => Promise.reject(getError(err))),
    patch: (url, data, options) =>
      ajax
        .patch(url, data || {}, {
          ...(options || {}),
        })
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => Promise.reject(getError(err))),
    fetch: (method, url, data, options) => {
      const config = {
        method,
        url,
        responseType: "json",
      };
      if (data) {
        if (method.toLowerCase() === "get") {
          config.params = data;
        } else {
          config.data = data;
        }
      }
      const args = { ...config, ...(options || {}) };
      return ajax(args)
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => Promise.reject(getError(err)));
    },
  };
  return {
    ...methods,
    postForm: (url, data, options) => {
      const opts = {
        ...options,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      return methods.post(url, data, opts);
    },
    getByteArray: (url, data, options) => {
      const config = { ...(options || {}), responseType: "arraybuffer" };
      ajax
        .get(url, { params: data, ...config })
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => {
          const { response } = err;
          if (
            response.request.responseType === "arraybuffer" &&
            response.data.toString() === "[object ArrayBuffer]"
          ) {
            const res = JSON.parse(Buffer.from(response.data).toString("utf8"));
            return Promise.reject(new Error(res.Error));
          }
          return Promise.reject(getError(err));
        });
    },
    postByteArray: (url, data, options) => {
      const config = { ...(options || {}), responseType: "arraybuffer" };
      ajax
        .post(url, data, config)
        .then((resp) => Promise.resolve(resp.data))
        .catch((err) => {
          const { response } = err;
          if (
            response.request.responseType === "arraybuffer" &&
            response.data.toString() === "[object ArrayBuffer]"
          ) {
            const res = JSON.parse(Buffer.from(response.data).toString("utf8"));
            return Promise.reject(new Error(res.Error));
          }
          return Promise.reject(getError(err));
        });
    },
  };
};
export default useAjax;

export const httpMethods = {
  GET: "get",
  POST: "post",
  PUT: "put",
  DEL: "delete",
  PATCH: "patch",
};
